import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/firebaseconfig';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { Send, Plus, Trash2, Zap, Hash, Calendar, Map, Tag, AlignLeft, UserCircle, Image } from 'lucide-react';
import AdminSidebar from './adminsidebar';

// 1. DEFINE YOUR FIXED STEPS HERE
const FIXED_STEPS = [
  "Site Feasibility",
  "Document Verification",
  "Document Compliance",
  "Application of DISCOM",
  "Site Work Of Civil & Electrical",
  "Equipment Installation (A. HTMC Panel, B. VCB, C. Transformer, D. LT Panel)",
  "CEIG Approval of Site",
  "Equipment Approval from DISCOM",
  "NOC from CEIG for Project",
  "Meter Released & Charging up the Site",
  "Punch Point Rectification",
  "HOTO (Handing Over to Operation Team) Done",
  "Site Completed"
];

const AdminAddProject = () => {
  const formRef = useRef(null);

  const initialProjectState = {
    title: '', 
    client: '', clientId: '', 
    category: '', 
    city: '', 
    state: '', stateId: '', 
    kilowatt: '', percentage: '', desc: '', fullDesc: '', 
    imageUrl: '', 
    projectNumber: '', timeline: ''      
  };
  
  // Initialize with the first step pre-selected to save time
  const initialStepsState = [{ title: FIXED_STEPS[0], desc: '', status: 'pending' }];

  const [project, setProject] = useState(initialProjectState);
  const [steps, setSteps] = useState(initialStepsState);
  const [clientsList, setClientsList] = useState([]);
  const [statesList, setStatesList] = useState([]); 
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientSnap = await getDocs(collection(db, "clients"));
        const stateSnap = await getDocs(collection(db, "states"));
        setClientsList(clientSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setStatesList(stateSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "clientId") {
      const selected = clientsList.find(c => c.id === value);
      setProject({ ...project, clientId: value, client: selected ? selected.name : '' });
    } 
    else if (name === "stateId") {
      const selected = statesList.find(s => s.id === value);
      setProject({ ...project, stateId: value, state: selected ? selected.name : '' });
    } 
    else {
      setProject({ ...project, [name]: value });
    }
  };

  const handleStepChange = (index, e) => {
    const newSteps = [...steps];
    newSteps[index][e.target.name] = e.target.value;
    setSteps(newSteps);
  };

  // When adding a step, default to the next one in the list based on current length
  const addStep = () => {
    const nextStepIndex = steps.length < FIXED_STEPS.length ? steps.length : 0;
    setSteps([...steps, { title: FIXED_STEPS[nextStepIndex], desc: '', status: 'pending' }]);
  };

  const removeStep = (index) => setSteps(steps.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project.clientId) return alert("Please select a client!");
    if (!project.stateId) return alert("Please select a state!");

    try {
      await addDoc(collection(db, "projects"), {
        ...project,
        kilowatt: Number(project.kilowatt),
        percentage: Number(project.percentage),
        steps: steps,
        createdAt: serverTimestamp()
      });
      
      alert("Project Successfully Added!");
      setProject(initialProjectState);
      setSteps(initialStepsState);
      if (formRef.current) formRef.current.reset();
    } catch (error) {
      console.error("Error adding project: ", error);
      alert("Error adding project.");
    }
  };

  return (
        <div className="container-fluid">
          <div className="">
            <div className="max-w-800 mx-auto bg-white p-5 rounded-4 shadow-sm">
              <h3 className="fw-bold mb-4 text-success d-flex align-items-center">
                <Zap className="me-2" /> Add New Project
              </h3>

              <form onSubmit={handleSubmit} ref={formRef}>
                {/* ... (Project fields same as before) ... */}
                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <label className="small text-muted">Project Title</label>
                    <input type="text" name="title" className="form-control bg-light border-0" onChange={handleChange} required value={project.title} />
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted d-flex align-items-center"><UserCircle size={14} className="me-1" /> Select Client</label>
                    <select name="clientId" className="form-select bg-light border-0" onChange={handleChange} required value={project.clientId}>
                      <option value="">{loadingData ? "Loading..." : "-- Select Client --"}</option>
                      {clientsList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted d-flex align-items-center"><Map size={14} className="me-1" /> Select State</label>
                    <select name="stateId" className="form-select bg-light border-0" onChange={handleChange} required value={project.stateId}>
                      <option value="">{loadingData ? "Loading..." : "-- Select State --"}</option>
                      {statesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6"><label className="small text-muted"><Hash size={14} /> Project Number</label><input type="text" name="projectNumber" className="form-control bg-light border-0" onChange={handleChange} value={project.projectNumber} /></div>
                  <div className="col-md-6"><label className="small text-muted"><Calendar size={14} /> Timeline</label><input type="text" name="timeline" className="form-control bg-light border-0" onChange={handleChange} value={project.timeline} /></div>
                  <div className="col-md-6"><label className="small text-muted"><Tag size={14} /> Category</label><input type="text" name="category" className="form-control bg-light border-0" onChange={handleChange} value={project.category} /></div>
                  <div className="col-md-6"><label className="small text-muted">City</label><input type="text" name="city" className="form-control bg-light border-0" onChange={handleChange} value={project.city} /></div>
                  <div className="col-md-6"><label className="small text-muted">Capacity (kW)</label><input type="number" name="kilowatt" className="form-control bg-light border-0" onChange={handleChange} value={project.kilowatt} /></div>
                  <div className="col-md-6"><label className="small text-muted">Progress %</label><input type="number" name="percentage" className="form-control bg-light border-0" onChange={handleChange} value={project.percentage} /></div>
                </div>

                <div className="mb-4">
                  <label className="small text-muted d-flex align-items-center"><Image size={14} className="me-1" /> Project Image Link</label>
                  <input type="text" name="imageUrl" className="form-control bg-light border-0" onChange={handleChange} value={project.imageUrl} />
                </div>

                <div className="mb-4"><label className="small text-muted"><AlignLeft size={14} /> Short Description</label><input type="text" name="desc" className="form-control bg-light border-0" onChange={handleChange} value={project.desc} /></div>
                <label className="small text-muted">Detailed Description</label><textarea name="fullDesc" rows="4" className="form-control bg-light border-0 mb-4" onChange={handleChange} value={project.fullDesc}></textarea>

                <hr />
                <h5 className="fw-bold mb-3 mt-4">Execution Roadmap</h5>
                
                {steps.map((step, index) => (
                  <div key={index} className="p-3 bg-light rounded-3 mb-3 position-relative border border-white">
                    <div className="row g-2">
                      <div className="col-md-8">
                        <label className="small text-muted mb-1">Step Name</label>
                        {/* 2. DROPDOWN REPLACES INPUT HERE */}
                        <select 
                          name="title" 
                          className="form-select form-select-sm fw-bold border-0" 
                          onChange={(e) => handleStepChange(index, e)} 
                          value={step.title}
                        >
                          {FIXED_STEPS.map((option, i) => (
                            <option key={i} value={option}>{i + 1}. {option}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted mb-1">Status</label>
                        <select name="status" className="form-select form-select-sm border-0" onChange={(e) => handleStepChange(index, e)} value={step.status}>
                          <option value="pending">Pending</option>
                          <option value="in-progress">In-Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                      <div className="col-md-11 mt-2">
                        <input type="text" name="desc" placeholder="Add specific notes for this step..." className="form-control form-control-sm border-0" onChange={(e) => handleStepChange(index, e)} value={step.desc} />
                      </div>
                      {steps.length > 1 && (
                        <div className="col-md-1 mt-2 text-end">
                          <button type="button" className="btn btn-sm text-danger" onClick={() => removeStep(index)}><Trash2 size={16} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button type="button" className="btn btn-outline-success btn-sm mb-5 shadow-sm" onClick={addStep}>
                  <Plus size={14} className="me-1" /> Next Roadmap Step
                </button>
                <button type="submit" className="btn btn-success w-100 py-3 fw-bold rounded-pill shadow-sm">PUBLISH TO WEBSITE</button>
              </form>
            </div>
          </div>
        </div>
    
  );
};

export default AdminAddProject;